import React, { useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import EqualisationsConcertina from 'components/EqualisationElements/EqualisationsConcertina';
import { numberToCurrencyString, pluralize } from 'further-ui/utils';
import { LinkedCapitalAction } from 'further-types/lp/capital-action';
import InteractiveTable from 'components/InteractiveTable';
import Heading from 'components/Heading';
import { useEqualisationSection } from '../EqualisationContext';

interface CapitalActionCardProps {
  capitalAction: LinkedCapitalAction;
  index: number;
  isLastElement: boolean;
  baseCurrency: string;
}

const CapitalActionName: React.FC<{
  name: string;
  capitalActionHeader: {
    investments: number;
    expenses: number;
    capitalRefunds: number;
    additionalPayments: number;
  };
}> = ({ name, capitalActionHeader }) => {
  const items = [
    { count: capitalActionHeader?.investments, label: 'investment' },
    { count: capitalActionHeader?.expenses, label: 'expense' },
    { count: capitalActionHeader?.capitalRefunds, label: 'capital refund' },
    {
      count: capitalActionHeader?.additionalPayments,
      label: 'additional payment',
    },
  ];

  const subtitle = items
    .filter((item) => (item.count ?? 0) > 0)
    .map((item) => `${item.count} ${pluralize(item.label, item.count)}`)
    .join(', ');

  return (
    <Box>
      <Typography variant="body1" key="capital-action-name">
        {name ?? ''}
      </Typography>
      <Typography variant="body1" key="capital-action-header">
        {subtitle}
      </Typography>
    </Box>
  );
};

const CapitalActionCard: React.FC<CapitalActionCardProps> = ({
  capitalAction,
  index,
  isLastElement,
  baseCurrency,
}) => {
  const { closeDate } = useEqualisationSection();

  const [activeTab, setActiveTab] = useState(1);
  const [rows, setRows] = useState(capitalAction.newInvestorBreakdown);

  const handleTabChange = (_: unknown, newValue: number) => {
    setActiveTab(newValue);
    if (newValue === 0) {
      setRows(
        capitalAction.originalInvestorBreakdown.map((row) => ({
          ...row,
          callOverpaidOrUnderpaid: 0,
        })),
      );
    } else {
      setRows(capitalAction.newInvestorBreakdown);
    }
  };

  const columns = [
    {
      field: 'investorName',
      headerName: 'Name',
      flex: 1,
    },
    {
      field: 'investments',
      headerName: 'Call - Investments',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.investments, {
          fallback: '-',
          currency: row.baseCurrency,
        }),
      flex: 1,
    },
    {
      field: 'expenses',
      headerName: 'Call - Expenses',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.expenses, {
          fallback: '-',
          currency: row.baseCurrency,
        }),
      flex: 1,
    },
    {
      field: 'returnOfCapital',
      headerName: 'Capital refunds',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.returnOfCapital, {
          fallback: '-',
          currency: row.baseCurrency,
        }),
      flex: 1,
    },
    {
      field: 'netCallOrDistribution',
      headerName: 'Net call/(distribution)',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.netCallOrDistribution, {
          fallback: '-',
          currency: row.baseCurrency,
          negativeInBrackets: true,
        }),
      flex: 1,
    },
    ...(activeTab === 1
      ? [
          {
            field: 'callOverpaidOrUnderpaid',
            headerName: 'Call underpaid/(overpaid)',
            renderCell: ({ row }) =>
              numberToCurrencyString(row.callOverpaidOrUnderpaid, {
                fallback: '-',
                currency: row.baseCurrency,
                negativeInBrackets: true,
              }),
            flex: 1,
          },
        ]
      : []),
  ];

  return (
    <EqualisationsConcertina
      key={index}
      gridTemplateColumns="60% 20% 18% 2%"
      isLastElement={isLastElement}
      expandedContent={
        <>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
          >
            <Tab
              label={
                <Heading variant="h5" noMargin>
                  Allocation before {closeDate?.toLocalDateLabel()}
                </Heading>
              }
            />
            <Tab
              label={
                <Heading variant="h5" noMargin>
                  Allocation after {closeDate?.toLocalDateLabel()}
                </Heading>
              }
            />
          </Tabs>
          <InteractiveTable
            id="investor-breakdown-table"
            columns={columns}
            rows={rows}
          />
        </>
      }
      concertinaItems={[
        <CapitalActionName
          name={capitalAction?.name ?? ''}
          capitalActionHeader={capitalAction.capitalActionHeader ?? {}}
          key="capital-action-name"
        />,
        <Typography variant="body1" key="ledger-date">
          {capitalAction?.ledgerDate
            ? capitalAction.ledgerDate.toLocalDateLabel()
            : '-'}
        </Typography>,
        <Typography variant="body1" key="total-amount">
          {numberToCurrencyString(capitalAction.totalAmountLcy, {
            fallback: '-',
            currency: baseCurrency,
            negativeInBrackets: true,
          })}
        </Typography>,
      ]}
    />
  );
};

export default CapitalActionCard;
